import React, { useEffect, useRef, useState } from "react";
import classes from "./Carousel.module.scss";
import CarouselItem from "./CarouselItem";
import VideoCard from "./VideoCard";
import VideoPlayerModal from "./VideoPlayerModal";
import WorldMap from "../controls/WorldMap";
import { Carousel as ReactCarousel } from "react-responsive-carousel";
import { CountryDataModel } from "../../models/DashboardModel";
import { useTranslation } from "react-i18next";
import Loading from "../common/Loading";
import MultiSelectModel from "../../models/MultiSelectModel";
import AutoComplete from "../controls/AutoComplete";
import {
  DashboardStatisticsModel,
  DashboardYearWiseStatisticsModel,
} from "../../models/DashboardStatisticsModel";
import {
  getStaticDashboardStatistics,
  getStaticYearWiseStatistics,
  getStaticAvailableYears
} from "../../data/staticSurveillanceData";
import { commonServices } from "../../services/commonService";
import { VideoTutorialModel } from "../../models/VideoTutorialModel";

type CarouselProps = {
  canRenderAssessmentStatistics: boolean;
};

type VideoTutorialConfig = {
  key: string;
  titleKey: string;
  durationKey: string;
};

// Single source of truth for every video tutorial shown on the Landing page carousel -
// the intro "see-how-it-works" teaser and the 3 cards on the "Video Tutorials" slide.
const VIDEO_TUTORIALS: VideoTutorialConfig[] = [
  {
    key: "see-how-it-works",
    titleKey: "Landing.VideoTutorials.SeeHowItWorks",
    durationKey: "Landing.VideoTutorials.OneMinVideoOverview",
  },
  {
    key: "tutorial-overview",
    titleKey: "Landing.VideoTutorials.TutorialOverview",
    durationKey: "Landing.VideoTutorials.OneMinVideoOverview",
  },
  {
    key: "conducting-assessment",
    titleKey: "Landing.VideoTutorials.ConductingAssessment",
    durationKey: "Landing.VideoTutorials.TwoMinVideoOverview",
  },
  {
    key: "user-roles-access",
    titleKey: "Landing.VideoTutorials.UserRolesAndAccess",
    durationKey: "Landing.VideoTutorials.OneMinVideoOverview",
  },
];

/** Renders Carousel component */
function Carousel(props: CarouselProps) {
  const { t } = useTranslation();
  const { canRenderAssessmentStatistics } = props;
  
  // Get distinct years from static surveillance data
  const years = getStaticAvailableYears();

  const [assessmentStatisticsDetails, setAssessmentStatisticsDetails] =
    useState<DashboardStatisticsModel>(DashboardStatisticsModel.init());

  const [
    assessmentYearWiseStatisticsDetails,
    setAssessmentYearWiseStatisticsDetails,
  ] = useState<DashboardYearWiseStatisticsModel>(
    DashboardYearWiseStatisticsModel.init()
  );

  const [loading, setLoading] = useState<boolean>(false);

  const [selectedYear, setSelectedYear] = useState<MultiSelectModel>(
    MultiSelectModel.initModel()
  );

  const [activeVideoKey, setActiveVideoKey] = useState<string | null>(null);

  const [videoTutorialsByKey, setVideoTutorialsByKey] = useState<
    Record<string, VideoTutorialModel>
  >({});

  // renders a VideoCard for a given video key, sourcing its thumbnail from the backend
  // and its title/duration from the single VIDEO_TUTORIALS config above
  const renderVideoCard = (videoKey: string) => {
    const config = VIDEO_TUTORIALS.find((video) => video.key === videoKey);
    if (!config) {
      return null;
    }

    return (
      <VideoCard
        videoKey={config.key}
        thumbnailUrl={videoTutorialsByKey[config.key]?.thumbnailUrl || ""}
        title={t(config.titleKey)}
        duration={t(config.durationKey)}
        onPlay={setActiveVideoKey}
      />
    );
  };

  const activeVideoConfig = VIDEO_TUTORIALS.find(
    (video) => video.key === activeVideoKey
  );

  // Close the video modal whenever the carousel moves to a different slide
  const onSlideChange = () => {
    setActiveVideoKey(null);
  };

  // Tracks the map's rendered size so the video modal can match it exactly
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapSize, setMapSize] = useState<{ width: number; height: number } | null>(
    null
  );

  useEffect(() => {
    const mapContainer = mapContainerRef.current;
    if (!mapContainer) {
      return;
    }

    const updateMapSize = () => {
      const rect = mapContainer.getBoundingClientRect();
      setMapSize({ width: rect.width, height: rect.height });
    };

    updateMapSize();

    const resizeObserver = new ResizeObserver(updateMapSize);
    resizeObserver.observe(mapContainer);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    // Always use static surveillance data for dashboard display
    // This ensures public users see assessment data even without API data
    const staticData = getStaticDashboardStatistics();
    setAssessmentStatisticsDetails(staticData);

    // Video stream/thumbnail URLs are backend-authored - the frontend just renders whatever it gets back
    commonServices.getVideoTutorials().then((videos: VideoTutorialModel[]) => {
      const byKey: Record<string, VideoTutorialModel> = {};
      (videos || []).forEach((video) => {
        byKey[video.key] = video;
      });
      setVideoTutorialsByKey(byKey);
    });
  }, []);

  // Get year wise assessment statistics from static data
  const getYearWiseAssessmentStatisticsDetails = (year: string) => {
    const staticYearData = getStaticYearWiseStatistics(year);
    setAssessmentYearWiseStatisticsDetails(staticYearData);
  };

  if (loading) {
    return <Loading />;
  }

  const {
    inProgressSurveillanceAssessments,
    completedSurveillanceAssessments,
    multipleCompletedSurveillanceAssessments,
    rapidSurveillanceAssessments,
    tailoredSurveillanceAssessments,
    comprehensiveSurveillanceAssessments,
  } = assessmentStatisticsDetails;

  const { inProgressAssessments, completedAssessments } =
    assessmentYearWiseStatisticsDetails;

  // generate country data for dashboard
  const generateCountryData = (): Array<Array<CountryDataModel>> => {
    return [
      [
        ...(inProgressSurveillanceAssessments || []),
        ...(completedSurveillanceAssessments || []),
        ...(multipleCompletedSurveillanceAssessments || []),
      ],
      [
        ...(rapidSurveillanceAssessments || []),
        ...(tailoredSurveillanceAssessments || []),
        ...(comprehensiveSurveillanceAssessments || []),
      ],
      [...(inProgressAssessments || []), ...(completedAssessments || [])],
    ];
  };

  const countryData: Array<Array<CountryDataModel>> = generateCountryData();

  // triggers whenever user add/removes items in autocomplete dropdown
  const onYearChange = (selectedItems: MultiSelectModel | null) => {
    if (selectedItems != null) {
      getYearWiseAssessmentStatisticsDetails(selectedItems.id.toString());
      setSelectedYear(selectedItems);
    } else {
      setAssessmentYearWiseStatisticsDetails(
        DashboardYearWiseStatisticsModel.init()
      );
      setSelectedYear(MultiSelectModel.initModel());
    }
  };

  return (
    <>
      <div className={classes.carouselWrapper}>
        <div
          id="app-carousel"
          className="carousel slide mb-4"
          data-ride="carousel"
        >
          <div className="carousel-inner">
            <ReactCarousel
              showArrows={true}
              showThumbs={false}
              showIndicators={true}
              showStatus={false}
              onChange={onSlideChange}
            >
              <div className={`row justify-content-end ${classes.mapStatsRow}`}>
                <div className="col-xs-12 col-sm-12 col-md-8" ref={mapContainerRef}>
                  <WorldMap countries={countryData[0]} />
                </div>

                {canRenderAssessmentStatistics && (
                  <div className="col-xs-12 col-sm-12 col-md-4">
                    <div className="carousel-caption text-left">
                      <ul className={classes.carousellist}>
                        <CarouselItem
                          className={`assessment-icon-2`}
                          title={t("Landing.Carousel.TotalInitiatedAssesments")}
                          count={`${
                            (inProgressSurveillanceAssessments || []).length ||
                            "0"
                          }
                                                    ${t("Common.Countries")}`}
                        />
                        <CarouselItem
                          className={`assessment-icon-3`}
                          title={t(
                            "Landing.Carousel.TotalNumberOfCountriesWithOneAssesment"
                          )}
                          count={`${
                            (completedSurveillanceAssessments || []).length ||
                            "0"
                          }
                                                         ${t(
                                                           "Common.Countries"
                                                         )}`}
                        />
                        <CarouselItem
                          className={`assessment-icon-4`}
                          title={t(
                            "Landing.Carousel.TotalNumberOfCountriesWithMoreThanOneAssesment"
                          )}
                          count={`${
                            (multipleCompletedSurveillanceAssessments || [])
                              .length || "0"
                          } ${t("Common.Countries")}`}
                        />
                      </ul>

                      {renderVideoCard("see-how-it-works")}
                    </div>
                  </div>
                )}
              </div>

              <div className={`row justify-content-end ${classes.mapStatsRow}`}>
                <div className="col-xs-12 col-sm-12 col-md-8">
                  <WorldMap countries={countryData[1]} />
                </div>
                {canRenderAssessmentStatistics && (
                  <div className="col-xs-12 col-sm-12 col-md-4">
                    <div className="carousel-caption text-left">
                      <ul className={classes.carousellist}>
                        <CarouselItem
                          className={`assessment-icon-2`}
                          title={t(
                            "Landing.Carousel.TotalCompletedRapidAssessments"
                          )}
                          count={`${
                            (rapidSurveillanceAssessments || []).length || "0"
                          }`}
                        />
                        <CarouselItem
                          className={`assessment-icon-3`}
                          title={t(
                            "Landing.Carousel.TotalCompletedTailoredAssesments"
                          )}
                          count={`${
                            (tailoredSurveillanceAssessments || []).length ||
                            "0"
                          }`}
                        />
                        <CarouselItem
                          className={`assessment-icon-4`}
                          title={t(
                            "Landing.Carousel.TotalCompletedComprehensiveAssesments"
                          )}
                          count={`${
                            (comprehensiveSurveillanceAssessments || [])
                              .length || "0"
                          }`}
                        />
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              <div className={`row justify-content-end ${classes.mapStatsRow}`}>
                <div className="col-xs-12 col-sm-12 col-md-8">
                  <WorldMap countries={countryData[2]} />
                </div>
                {canRenderAssessmentStatistics && (
                  <div className="col-xs-12 col-sm-12 col-md-4">
                    <div className="carousel-caption text-left">
                      <p className="my-3">
                        {t("Landing.Carousel.TotalYearWiseAssessments")}
                      </p>

                      <AutoComplete
                        id="years"
                        label={t("Landing.Carousel.SelectAssessmentsYear")}
                        values={selectedYear}
                        options={years.map((year) => {
                          return new MultiSelectModel(
                            year,
                            year.toString(),
                            false,
                            false
                          );
                        })}
                        placeholder={t("Common.SelectYear")}
                        onUpdate={(selectedItems: MultiSelectModel | null) =>
                          onYearChange(selectedItems)
                        }
                      />

                      <ul className={classes.carousellist}>
                        <CarouselItem
                          className={`assessment-icon-2`}
                          title={t("Landing.Carousel.TotalInitiatedAssesments")}
                          count={`${
                            (inProgressAssessments || []).length || "0"
                          }`}
                        />
                        <CarouselItem
                          className={`assessment-icon-4`}
                          title={t(
                            "Landing.Carousel.CompletedAssesmentCountries"
                          )}
                          count={`${
                            (completedAssessments || []).length || "0"
                          }`}
                        />
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              <div className={`row ${classes.videoTutorialsSlide}`}>
                <div className="col-xs-12">
                  <h4 className="mb-3">
                    {t("Landing.VideoTutorials.Heading")}
                  </h4>
                </div>

                <div className={`row ${classes.videoTutorialsCards}`}>
                  {VIDEO_TUTORIALS.filter(
                    (video) => video.key !== "see-how-it-works"
                  ).map((video) => (
                    <div className="col-sm-12 col-md-4 mb-3" key={video.key}>
                      {renderVideoCard(video.key)}
                    </div>
                  ))}
                </div>
              </div>
            </ReactCarousel>
          </div>
          <small className="d-flex mt-2 fst-italic">
            * {t("Common.GeographicRegionsNote")}
          </small>

          <VideoPlayerModal
            streamUrl={
              activeVideoKey
                ? videoTutorialsByKey[activeVideoKey]?.streamUrl || null
                : null
            }
            title={activeVideoConfig ? t(activeVideoConfig.titleKey) : undefined}
            width={mapSize?.width}
            height={mapSize?.height}
            onDialogClose={() => setActiveVideoKey(null)}
          />
        </div>
      </div>
    </>
  );
}

export default Carousel;
